import "./locationPrimaryContactDetailsAddComponent.scss";
import * as Yup from "yup";
import React, {useCallback, useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../../store/reducers";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import CommonService from "../../../../shared/services/common.service";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import FormTitleWithNavigationBack
    from "../../../../shared/components/form-title-with-navigation-back/FormTitleWithNavigationBack";
import Grid from "@mui/material/Grid";
import FormikInputComponent from "../../../../shared/components/core/formik-input/FormikInputComponent";
import ButtonComponent from "../../../../shared/components/core/button/ButtonComponent";
import FormikAutoCompleteDropdown
    from "../../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import FromikPhoneNumberComponent
    from "../../../../shared/components/core/fromik-phone-number/FromikPhoneNumberComponent";
import commonService from "../../../../shared/services/common.service";
import {useSearchParams} from "react-router-dom";
import LinkComponent from "../../../../shared/components/link/LinkComponent";

interface LocationPrimaryContactDetailsAddComponentProps {
    locationId: number;
    next: Function;
    cancel: Function;
    prev: Function;
    locationDetails: any;
}

const LocationPrimaryContactDetailsValidationSchema = Yup.object({
    isSameAsAdmin: Yup.boolean().required("Please select one option"),
    firstName: Yup.string().when('isSameAsAdmin', {
        is: (isSameAsAdmin: boolean) => isSameAsAdmin === false,
        then: Yup.string().required("First Name  is required"),
    }),
    lastName: Yup.string().when('isSameAsAdmin', {
        is: (isSameAsAdmin: boolean) => isSameAsAdmin === false,
        then: Yup.string().required("Last Name  is required"),
    }),
    middleName: Yup.string().when('isSameAsAdmin', {
        is: (isSameAsAdmin: boolean) => isSameAsAdmin === false,
        then: Yup.string().nullable()
    }),
    emailId: Yup.string().when('isSameAsAdmin', {
        is: (isSameAsAdmin: boolean) => isSameAsAdmin === false,
        then: Yup.string()
            .nullable()
            .email("Invalid Email ID")
            .required("Email ID is required")
            .matches(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/, "Invalid Email ID")
    }),
    mobileNumber: Yup.string().when('isSameAsAdmin', {
        is: (isSameAsAdmin: boolean) => isSameAsAdmin === false,
        then: Yup.string()
            .nullable()
            .required("Mobile Number is required")
            .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits"),

    }),
    faxNumber: Yup.string().when('isSameAsAdmin', {
        is: (isSameAsAdmin: boolean) => isSameAsAdmin === false,
        then: Yup.string()
            .nullable()
            .matches(/\d+/, 'only digits allowed')
            .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Fax Number should be 10 digits"),
    }),
    secondaryMobileNumber: Yup.string().nullable().when('isSameAsAdmin', {
        is: (isSameAsAdmin: boolean) => isSameAsAdmin === false,
        then: Yup.string()
            .nullable()
            .matches(/\d+/, 'only digits allowed')
            .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits")
    }),
    accountExecutiveId: Yup.mixed().required("Account Executive is required"),
});

interface LocationPrimaryContactDetailsFormSchema {
    isSameAsAdmin: boolean;
    firstName: string;
    lastName: string;
    middleName: string;
    mobileNumber: string;
    emailId: string;
    secondaryMobileNumber: string;
    faxNumber: string;
    accountExecutiveId: string;
}

const formInitialValues: LocationPrimaryContactDetailsFormSchema = {
    isSameAsAdmin: false,
    firstName: "",
    lastName: "",
    middleName: "",
    mobileNumber: "",
    emailId: "",
    secondaryMobileNumber: "",
    faxNumber: "",
    accountExecutiveId: "",
}
const LocationPrimaryContactDetailsAddComponent = (props: LocationPrimaryContactDetailsAddComponentProps) => {

    const {locationId, prev, next, cancel, locationDetails} = props;
    const [initialValues, setInitialValues] = useState<LocationPrimaryContactDetailsFormSchema>(formInitialValues);
    const {accountExecutiveList} = useSelector((state: IRootReducerState) => state.staticData);
    const [searchParams] = useSearchParams();
    const lastCompletedStep = searchParams.get("lastCompletedStep");

    const onSubmit = useCallback((values: any, {
        setSubmitting,
        setErrors,
    }: FormikHelpers<any>) => {
        const primaryContactDetails = {...values};
        primaryContactDetails["mobileNumber"] = primaryContactDetails["mobileNumber"]?.replace(/[^0-9]/g, "");
        primaryContactDetails["secondaryMobileNumber"] = primaryContactDetails["secondaryMobileNumber"]?.replace(/[^0-9]/g, "");
        primaryContactDetails["faxNumber"] = primaryContactDetails["faxNumber"]?.replace(/[^0-9]/g, "");
        const payload = {
            primaryContactDetails: primaryContactDetails,
            accountExecutiveId: primaryContactDetails.accountExecutiveId.id,
            isSameAsAdmin: primaryContactDetails.isSameAsAdmin,
            lastCompletedStep: lastCompletedStep
        };
        if (payload.primaryContactDetails.isSameAsAdmin) {
            delete payload.primaryContactDetails;
        }
        setSubmitting(true);
        CommonService._location.addPrimaryContactDetails(locationId, payload)
            .then((response: IAPIResponseType<any>) => {
                setSubmitting(false);
                const primaryContactDetailsObj = response.data;
                primaryContactDetailsObj.primaryContactDetails.accountExecutiveId = response.data.primaryContactDetails.accountExecutiveDetails;
                next(primaryContactDetailsObj);
                CommonService._alert.showToast(response?.message || 'primary Contact details has been added', "success");
            })
            .catch((error: any) => {
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
                CommonService._alert.showToast(error.error || "Something went wrong!", "error")
            })
    }, [locationId, next, lastCompletedStep]);

    const handlePopulateAdminDetails = useCallback((setFieldValue: (field: string, value: any, shouldValidate?: (boolean | undefined)) => void) => {
        if (!locationDetails?.adminId) {
            CommonService._alert.showToast("Admin is not assigned to this location", "warning");
            return;
        }
        const {
            firstName = '',
            middleName = '',
            lastName = '',
            mobileNumber = '',
            secondaryMobileNumber = '',
            faxNumber = '',
            emailId = ''
        } = locationDetails?.adminId ? (locationDetails?.adminDetails || {}) : {};

        setFieldValue('firstName', firstName)
        setFieldValue('lastName', lastName)
        setFieldValue('middleName', middleName)
        setFieldValue('mobileNumber', CommonService.getMaskedMobileNumber(mobileNumber))
        setFieldValue('secondaryMobileNumber', CommonService.getMaskedMobileNumber(secondaryMobileNumber))
        setFieldValue('faxNumber', CommonService.getMaskedMobileNumber(faxNumber))
        setFieldValue('emailId', emailId)
    }, [locationDetails?.adminDetails, locationDetails?.adminId])

    useEffect(() => {
        if (locationDetails?.primaryContactDetails) {
            const primaryContactDetails = locationDetails.primaryContactDetails;
            const initialValues: LocationPrimaryContactDetailsFormSchema = {
                // isSameAsAdmin: locationDetails.primaryContactDetails.isSameAsAdmin,
                isSameAsAdmin: false,
                firstName: primaryContactDetails.firstName,
                lastName: primaryContactDetails.lastName,
                middleName: primaryContactDetails.middleName,
                mobileNumber: commonService.getMaskedMobileNumber(primaryContactDetails.mobileNumber),
                emailId: primaryContactDetails.emailId,
                secondaryMobileNumber: commonService.getMaskedMobileNumber(primaryContactDetails.secondaryMobileNumber),
                faxNumber: commonService.getMaskedMobileNumber(primaryContactDetails.faxNumber),
                accountExecutiveId: primaryContactDetails.accountExecutiveDetails,
            }
            setInitialValues(initialValues);
        }
    }, [locationDetails]);


    return (
        <div className={'primary-contact-details-add-component screen t-form-screen'}>
            <FormTitleWithNavigationBack
                title={'Primary Contact'}
                tooltipData={"These details pertains to the primary point of contact for the specified location, and the account executive is the AdvanzInnovation representative for this location."}
                handleClick={prev}
            />
            <div className="t-form">
                <Formik
                    validationSchema={LocationPrimaryContactDetailsValidationSchema}
                    initialValues={initialValues}
                    validateOnChange={false}
                    validateOnBlur={true}
                    enableReinitialize={true}
                    validateOnMount={true}
                    onSubmit={onSubmit}
                >
                    {({isSubmitting, errors, values, isValid, setFieldValue, validateForm}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [validateForm, values]);
                        return (
                            <Form className={"location-primary-contact-details-add-holder"}>
                                <div className="t-form-controls-wrapper">
                                    <div className="d-flex align-items-center mrg-bottom-10">
                                        <div className="redirect-new-link-wrapper mrg-bottom-20">
                                            <div>Fetch details of admin?</div>
                                            <LinkComponent
                                                className={"redirect-link"}
                                                onClick={() => handlePopulateAdminDetails(setFieldValue)}>
                                                Fetch Admin Details
                                            </LinkComponent>
                                        </div>
                                        {/*<div className="mrg-right-30 radio-button-title">Is the admin overseeing this*/}
                                        {/*    role?*/}
                                        {/*</div>*/}
                                        {/*<Field*/}
                                        {/*    name={'isSameAsAdmin'}*/}
                                        {/*>*/}
                                        {/*    {*/}
                                        {/*        (field: FieldProps) => (*/}
                                        {/*            <FormikRadioButtonGroupComponent*/}
                                        {/*                direction={"row"}*/}
                                        {/*                isValueBoolean={true}*/}
                                        {/*                titleKey={'title'}*/}
                                        {/*                valueKey={'code'}*/}
                                        {/*                options={CommonService._staticData.yesNoOptions}*/}
                                        {/*                formikField={field}*/}
                                        {/*            />*/}
                                        {/*        )*/}
                                        {/*    }*/}
                                        {/*</Field>*/}
                                    </div>
                                    <>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={12} md={6} xl={6}>
                                                <Field name={'firstName'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'First Name'}
                                                                placeholder={'Enter First Name'}
                                                                type={"text"}
                                                                required={true}
                                                                formikField={field}
                                                            />
                                                        )}
                                                </Field>
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={6} xl={6}>
                                                <Field name={'middleName'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Middle Name'}
                                                                placeholder={'Enter Middle Name'}
                                                                type={"text"}
                                                                required={false}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                        </Grid>

                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={12} md={6} xl={6}>
                                                <Field name={'lastName'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Last Name'}
                                                                placeholder={'Enter Last Name'}
                                                                type={"text"}
                                                                required={true}
                                                                formikField={field}
                                                            />
                                                        )}
                                                </Field>
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={6} xl={6}>
                                                <Field name={'emailId'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Email ID'}
                                                                placeholder={'Enter Email ID'}
                                                                type={"email"}
                                                                required={true}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'mobileNumber'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FromikPhoneNumberComponent
                                                                label={'Mobile Number'}
                                                                placeholder={'(000) 000-0000'}
                                                                maxLength={14}
                                                                required={true}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'secondaryMobileNumber'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FromikPhoneNumberComponent
                                                                label={'Secondary Mobile Number'}
                                                                placeholder={'(000) 000-0000'}
                                                                maxLength={14}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                        </Grid>
                                    </>
                                    <Grid container spacing={2}>
                                        {
                                            !values.isSameAsAdmin &&
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'faxNumber'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FromikPhoneNumberComponent
                                                                label={'Fax Number'}
                                                                placeholder={'(000) 000-0000'}
                                                                maxLength={14}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                        }
                                    </Grid>
                                    <div className={'horizontal-line'}/>
                                    <div className={'form-sub-title mrg-top-20 mrg-bottom-20'}>Account Executive</div>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'accountExecutiveId'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikAutoCompleteDropdown
                                                            label={'Account Executive'}
                                                            required={true}
                                                            data={accountExecutiveList}
                                                            valueKey={"id"}
                                                            placeholder={'Select Account Executive'}
                                                            formikField={field}
                                                            getOptionLabel={(option => (option?.firstName) || '')}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                </div>
                                <div className="form-action-section">
                                    <ButtonComponent
                                        type="button"
                                        color={"primary"}
                                        variant={"outlined"}
                                        fullWidth={true}
                                        disabled={isSubmitting}
                                        handleClick={cancel}
                                    >
                                        Close
                                    </ButtonComponent>
                                    <ButtonComponent
                                        type="submit"
                                        color={"primary"}
                                        variant={"contained"}
                                        fullWidth={true}
                                        disabled={!isValid}
                                        isLoading={isSubmitting}
                                    >
                                        Next
                                    </ButtonComponent>
                                </div>

                            </Form>
                        )
                    }
                    }
                </Formik>
            </div>
        </div>
    );

};

export default LocationPrimaryContactDetailsAddComponent;
